import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, Document } from '@contentful/rich-text-types';

type Props = {
  content: Document | null | undefined;
};

/**
 * Extract a YouTube video id from common URL shapes:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/shorts/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 */
function getYouTubeId(rawUrl: string): string | null {
  try {
    const url = new URL(rawUrl);

    const host = url.hostname.replace(/^www\./, '').toLowerCase();
    const isYouTube =
      host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com' || host === 'youtu.be';

    if (!isYouTube) return null;

    if (host === 'youtu.be') {
      const id = url.pathname.split('/').filter(Boolean)[0];
      return id || null;
    }

    const v = url.searchParams.get('v');
    if (v) return v;

    const parts = url.pathname.split('/').filter(Boolean);

    const shortsIndex = parts.indexOf('shorts');
    if (shortsIndex !== -1 && parts[shortsIndex + 1]) return parts[shortsIndex + 1];

    const embedIndex = parts.indexOf('embed');
    if (embedIndex !== -1 && parts[embedIndex + 1]) return parts[embedIndex + 1];

    return null;
  } catch {
    return null;
  }
}

/**
 * Supports start time:
 * - ?t=90
 * - ?t=1m30s
 * - ?start=90
 */
function toYouTubeEmbedUrl(rawUrl: string): string | null {
  const id = getYouTubeId(rawUrl);
  if (!id) return null;

  let start = 0;

  try {
    const url = new URL(rawUrl);
    const t = url.searchParams.get('t');
    const startParam = url.searchParams.get('start');

    const parseT = (val: string) => {
      if (/^\d+$/.test(val)) return Number(val);

      const m = val.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i);
      if (!m) return 0;

      const h = Number(m[1] || 0);
      const mi = Number(m[2] || 0);
      const s = Number(m[3] || 0);
      return h * 3600 + mi * 60 + s;
    };

    if (startParam && /^\d+$/.test(startParam)) start = Number(startParam);
    else if (t) start = parseT(t);
  } catch {
    // ignore
  }

  const qs = start > 0 ? `?start=${start}` : '';
  return `https://www.youtube.com/embed/${id}${qs}`;
}

export default function RichTextBlock({ content }: Props) {
  if (!content) return null;

  return (
    <div className="prose prose-lg max-w-none">
      {documentToReactComponents(content, {
        renderNode: {
          [BLOCKS.PARAGRAPH]: (_node, children) => <p className="text-neutral-700 mb-24">{children}</p>,
          [BLOCKS.HEADING_1]: (_node, children) => <h1 className="mt-6 mb-2 text-4xl font-semibold">{children}</h1>,
          [BLOCKS.HEADING_2]: (_node, children) => <h2 className="mt-6 mb-2 text-xl font-bold">{children}</h2>,
          [BLOCKS.HEADING_3]: (_node, children) => <h3 className="mb-2 mt-4 text-lg font-bold">{children}</h3>,
          [BLOCKS.UL_LIST]: (_node, children) => <ul style={{ listStyle: 'outside' }}>{children}</ul>,
          [BLOCKS.LIST_ITEM]: (_node, children) => <li className="ml-16">{children}</li>,

          [INLINES.HYPERLINK]: (node, children) => {
            const uri: string = node?.data?.uri ?? '';
            const embedUrl = toYouTubeEmbedUrl(uri);

            if (embedUrl) {
              // Responsive 16:9 embed wrapper
              return (
                <span className="my-6 block">
                  <span className="relative block w-full overflow-hidden rounded-lg" style={{ paddingTop: '56.25%' }}>
                    <iframe
                      className="absolute inset-0 h-full w-full"
                      src={embedUrl}
                      title="YouTube video"
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </span>
                </span>
              );
            }

            return (
              <a href={uri} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                {children}
              </a>
            );
          },
        },
      })}
    </div>
  );
}
